import {useMemo, useState, useEffect} from "react";
import dayjs from "dayjs";
import {DashboardLayout} from "components/Layout";
import {BasicTable, SearchBox} from "components/Table";
import {DatePicker} from "components/DatePicker";
import {BasicDropdown} from "components/Dropdown";
import {DropdownAsync} from "components/Dropdown/DropdownAsync";
import {useStaffAttendanceSuperAdmin} from "hooks/SuperAdmin/Attendance";
import axios from "services/axios";
import {useHistory} from "react-router-dom";
import {SubmitButton} from "components/Button";

// Hospital options fetched per staff user from API
export function StaffAttendanceSuperAdminPage() {
	const [dateRange, setDateRange] = useState({startDate: undefined, endDate: undefined});
	const [dateDisplay, setDateDisplay] = useState("");
	const [dataLimit, setDataLimit] = useState(10);
	const [exporting, setExporting] = useState(false);
	const [filterResetKey, setFilterResetKey] = useState(0);
	const [durationSelectionsByUser, setDurationSelectionsByUser] = useState({});
	const [hospitalOptionsByUser, setHospitalOptionsByUser] = useState({});
	const history = useHistory();

	const formatDurationHM = secs => {
		const totalMinutes = Math.floor((secs || 0) / 60);
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;

		return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
	};

	const {data, setFilter, isLoading, filter} = useStaffAttendanceSuperAdmin();

	useEffect(() => {
		setFilter(prev => ({
			...prev,
			date: undefined,
			startDate: dateRange.startDate,
			endDate: dateRange.endDate,
			page: 1,
		}));
	}, [dateRange, setFilter]);

	const handleResetFilter = () => {
		setDateRange({startDate: undefined, endDate: undefined});
		setDateDisplay("");
		setDurationSelectionsByUser({});
		setFilterResetKey(prev => prev + 1);
		setFilter(prev => ({
			...prev,
			hospitalId: null,
			hospitalIds: [],
			date: undefined,
			startDate: undefined,
			endDate: undefined,
			page: 1,
			limit: dataLimit,
			search: "",
			orderBy: "checkInDate",
			sortBy: "DESC",
			durationHospitalIdsByUser: {},
		}));
	};

	const rows = useMemo(() => data?.data?.rows || [], [data?.data?.rows]);

	const columns = useMemo(
		() => [
			{Header: "No", accessor: "idSerial", width: 60, centered: true},
			{Header: "Nama Staff", accessor: "staffName"},
			{Header: "Durasi Jam Kerja", accessor: "durationHM", centered: true},
			{
				Header: "Rumah Sakit",
				accessor: "durationSelection",
				centered: true,
				Cell: ({row}) => {
					const staffId = row?.original?.staffId;
					const selectedIds = durationSelectionsByUser[staffId] || [];
					// Handle null/undefined staffId by rendering a disabled selector

					if (!staffId) {
						return (
							<div style={{minWidth: 220}}>
								<DropdownAsync
									defaultOptions={[]}
									disabled={true}
									isMulti={true}
									label="Staff tidak tersedia"
									value={[]}
								/>
							</div>
						);
					}

					return (
						<div style={{minWidth: 220}}>
							<StaffHospitalsSelector
								cachedOptions={hospitalOptionsByUser[staffId] || []}
								selectedIds={selectedIds}
								staffId={staffId}
								onChange={vals => {
									const ids = Array.isArray(vals) ? vals.map(v => v.value) : [];

									setDurationSelectionsByUser(prev => {
										const next = {...prev, [staffId]: ids};

										setFilter(f => ({
											...f,
											durationHospitalIdsByUser: next,
											page: 1,
										}));

										return next;
									});
								}}
								onOptionsLoaded={opts => setHospitalOptionsByUser(prev => ({...prev, [staffId]: opts}))}
							/>
						</div>
					);
				},
			},
			{
				Header: "Aksi",
				accessor: "action",
				centered: true,
				Cell: ({row}) => {
					const userId = row?.original?.userId;

					if (!userId) {
						return (
							<span class="text-muted" style={{cursor: "not-allowed"}}>
								Tidak tersedia
							</span>
						);
					}

					return (
						<a
							class="text-orange"
							style={{cursor: "pointer"}}
							onClick={() =>
								history.push({
									pathname: `/absensi-staff/detail/${userId}`,
									state: {
										startDate: filter?.startDate || dateRange.startDate,
										endDate: filter?.endDate || dateRange.endDate,
										search: filter?.search || "",
									},
								})
							}>
							View Detail
						</a>
					);
				},
			},
		],
		[
			durationSelectionsByUser,
			hospitalOptionsByUser,
			setFilter,
			filter?.startDate,
			filter?.endDate,
			filter?.search,
			dateRange.startDate,
			dateRange.endDate,
			history,
		],
	);

	const displayRows = useMemo(() => {
		return rows.map((r, idx) => ({
			...r,
			idSerial: r.idSerial ?? idx + 1,
			staffName: r.staffName ?? r?.user?.fullName ?? "-",
			durationHM: formatDurationHM(r?.userDurationSeconds),
		}));
	}, [rows]);

	// Inline component to fetch hospital options per staff user
	function StaffHospitalsSelector({staffId, selectedIds, cachedOptions, onOptionsLoaded, onChange}) {
		const [options, setOptions] = useState(cachedOptions);

		useEffect(() => {
			let mounted = true;
			const fetchOptions = async () => {
				try {
					if (!staffId) {
						if (mounted) setOptions([]);

						return;
					}
					const res = await axios.get(`/hospital/staff/${staffId}`);
					const list = Array.isArray(res?.data?.data) ? res.data.data : [];
					const mapped = list
						.map(item => ({
							label: item?.name ?? item?.hospitalName ?? item?.hospital?.name ?? "-",
							value: item?.id ?? item?.hospitalId ?? item?.hospital?.id,
						}))
						.filter(opt => opt.value != null);

					if (mounted) {
						setOptions(mapped);
						if (typeof onOptionsLoaded === "function") onOptionsLoaded(mapped);
					}
				} catch (e) {
					if (mounted) setOptions([]);
				}
			};

			if (!cachedOptions || cachedOptions.length === 0) fetchOptions();

			return () => {
				mounted = false;
			};
		}, [staffId, cachedOptions, onOptionsLoaded]);

		const value = options.filter(opt => selectedIds.includes(opt.value));

		return (
			<DropdownAsync
				defaultOptions={options}
				fetchOptions={async (_input, cb) => {
					try {
						if (!staffId) {
							if (typeof cb === "function") cb([]);

							return;
						}
						const res = await axios.get(`/hospital/staff/${staffId}`);
						const list = Array.isArray(res?.data?.data) ? res.data.data : [];
						const mapped = list
							.map(item => ({
								label: item?.name ?? item?.hospitalName ?? item?.hospital?.name ?? "-",
								value: item?.id ?? item?.hospitalId ?? item?.hospital?.id,
							}))
							.filter(opt => opt.value != null);

						setOptions(mapped);
						if (typeof cb === "function") cb(mapped);
						if (typeof onOptionsLoaded === "function") onOptionsLoaded(mapped);
					} catch (e) {
						if (typeof cb === "function") cb([]);
					}
				}}
				isMulti={true}
				label="Pilih Rumah Sakit"
				value={value}
				onChange={onChange}
			/>
		);
	}

	const handleExport = async () => {
		try {
			setExporting(true);
			const start = dateRange.startDate;
			const end = dateRange.endDate;
			const rawDurationMap = filter?.durationHospitalIdsByUser || {};
			const sanitizedDurationMap = Object.keys(rawDurationMap).reduce((acc, key) => {
				if (key && key !== "null" && key !== "undefined") {
					const ids = rawDurationMap[key];

					if (Array.isArray(ids) && ids.length > 0) acc[key] = ids;
				}

				return acc;
			}, {});

			const params = {
				search: filter?.search || undefined,
				orderBy: filter?.orderBy || "checkInDate",
				sortBy: filter?.sortBy || "DESC",
				...(Array.isArray(filter?.hospitalIds) && filter.hospitalIds.length > 0
					? {hospitalIds: filter.hospitalIds.join(",")}
					: {}),
				...(Object.keys(sanitizedDurationMap).length > 0
					? {durationHospitalIdsByUser: JSON.stringify(sanitizedDurationMap)}
					: {}),
			};

			if (start && end) params.checkInDate = `${start},${end}`;

			const res = await axios.get("/staff/attendances/export", {params, responseType: "blob"});
			const blob = res?.data;
			const disposition = res?.headers?.["content-disposition"] || res?.headers?.["Content-Disposition"];
			let filename = `absensi-staff-${dayjs().format("YYYYMMDD-HHmmss")}.xlsx`;

			if (disposition) {
				const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
				const name = decodeURIComponent(match?.[1] || match?.[2] || "");

				if (name) filename = name;
			}
			if (!/\.xlsx$/i.test(filename)) filename = filename.replace(/\.[^/.]+$/, "") + ".xlsx";

			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");

			link.href = url;
			link.download = filename;
			link.click();
			URL.revokeObjectURL(url);
		} catch (err) {
			alert("Gagal mengekspor data absensi. Coba lagi nanti.");
		} finally {
			setExporting(false);
		}
	};

	return (
		<DashboardLayout>
			<>
				<div class="text-bold text-dark mb-4">Absensi Staff</div>
				<SearchBox
					key={filterResetKey}
					rightElement={
						<div className="d-flex align-items-end gap-2">
							<div style={{minWidth: "220px"}}>
								<label class="setting-label">Rentang Waktu</label>
								<BasicDropdown
									data={[
										{label: "1 Minggu Lalu", value: "1w"},
										{label: "1 Bulan Lalu", value: "1m"},
										{label: "1 Tahun Lalu", value: "1y"},
									]}
									placeholder="Pilih rentang"
									onSelect={val => {
										const now = dayjs();
										let start;

										if (val === "1w") start = now.subtract(1, "week");
										else if (val === "1m") start = now.subtract(1, "month");
										else start = now.subtract(1, "year");
										const startDate = start.format("YYYY-MM-DD");
										const endDate = now.format("YYYY-MM-DD");

										setDateRange({startDate, endDate});
										setDateDisplay(`${start.format("MM/DD/YYYY")} - ${now.format("MM/DD/YYYY")}`);
									}}
								/>
							</div>
							<div style={{minWidth: "220px"}}>
								<label class="setting-label">Tanggal</label>
								<DatePicker
									initialSettings={{autoUpdateInput: false}}
									singleDatePicker={false}
									onChange={val => {
										const {startDate, endDate} = val || {};

										if (startDate && endDate) {
											setDateRange({startDate, endDate});
											setDateDisplay(
												`${dayjs(startDate).format("MM/DD/YYYY")} - ${dayjs(endDate).format(
													"MM/DD/YYYY",
												)}`,
											);
										}
									}}>
									<input
										class="form-control"
										placeholder="Pilih rentang tanggal"
										value={dateDisplay}
									/>
								</DatePicker>
							</div>
							<SubmitButton text="Reset filter" onClick={handleResetFilter} />

							<button class="btn btn-orange" disabled={exporting} onClick={handleExport}>
								Export Data
							</button>
						</div>
					}
					onSearch={keyword => {
						setFilter(prev => ({...prev, search: keyword || "", page: 1}));
					}}
				/>

				<BasicTable
					columns={columns}
					currentPage={data?.data?.currentPage || 1}
					data={displayRows}
					hidePagination={true}
					limit={dataLimit}
					loading={isLoading}
					striped={true}
					title="Data Absensi Staff"
					totalItems={data?.data?.totalItems ?? rows.length}
					totalPage={1}
					onLimitChange={limit => {
						setDataLimit(limit);
						setFilter(prev => ({...prev, limit}));
					}}
					onPageChange={() => {}}
				/>
			</>
		</DashboardLayout>
	);
}

export default StaffAttendanceSuperAdminPage;
